import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ESOganisationDropdown from "../components/dropdown/esOrganisationDropdown";
import SectionHeader from "../components/section/SectionHeader";
import {
  ADD_MCPTT_GROUPS_FORM_FIELDS,
  EDIT_MCPTT_GROUPS_FORM_FIELDS,
  EDIT_MCPTT_GROUP_PROFILE_FORM_FIELDS,
  MCPTT_GROUPS_FILTER_COLUMNS,
  MCPTT_GROUPS_TABLE_HEADERS,
} from "../constants";
import AuthContext from "../store/authContext";
import {
  createSearchConfig,
  errorAlert,
  makeApiRequest,
  successAlert,
  transformMCPTTGroupProfileUpdateBody,
  transformMCPTTGroupsRows,
  transformMCPTTGroupsUpdateBody,
} from "../util";
import DataTableWithControls from "../components/dataTable/DataTableWithControls";
import OrgContext from "../store/orgContext";
import InfoAlert from "../ui/InfoAlert";
import TableActionButton from "../ui/TableActionButton";
import AdvancedSearch from "../components/advancedSearch/AdvancedSearch";
import {
  deleteMCPTTGroup,
  getMCPTTGroupByRef,
  getMCPTTGroupDetailsByRef,
  getMCPTTGroupProfileByRef,
  updateMCPTTGroupByRef,
  updateMCPTTGroupProfileByRef,
} from "../api";
import EditGroup from "../components/dataTable/EditGroup";
import {
  INITIAL_MCPTT_GROUPS_EDIT_STATE,
  INITIAL_MCPTT_GROUP_PROFILE_EDIT_STATE,
} from "../constants/editState";
import useHideSidePanel from "../hooks/useHideSidePanel";
import {
  READ_MCPTT_GROUP_TRANSLATIONS,
  DELETE_MCPTT_GROUP_TRANSLATIONS,
} from "../translations/util";
import useAppTitle from "../hooks/useAppTitle";
import { MCGroupsEdit } from "../api/icCheck";
import SearchContext from "../store/searchContext";
import BulkUpload from "../components/bulkUpload/BulkUpload";

interface ISearchData {
  "MCPTT-GID"?: string | null;
  "MAPPED-P25-TG-ID"?: string | null;
  "MAPPED-DMR-TG-ID"?: string | null;
  "MAPPED-ROIP-TG-ID"?: string | null;
  "MAPPED-DFSI-TG-ID"?: string | null;
}

const INITIAL_STATE = {
  "MCPTT-GID": "",
  "MAPPED-P25-TG-ID": "",
  "MAPPED-DMR-TG-ID": "",
  "MAPPED-ROIP-TG-ID": "",
  "MAPPED-DFSI-TG-ID": "",
};

const searchFields = ["MCPTT-GID", "SELECT A MAPPED-FIELD"];

const searchConfigNew = createSearchConfig(
  searchFields,
  ADD_MCPTT_GROUPS_FORM_FIELDS
);

const MCPTTGroups = () => {
  const {
    token,
    scopes: { mc_groups: mcpttGroups, mc_group_profile: mcpttGroupProfile },
  } = useContext(AuthContext);
  const { orgs: orgList, selectedOrg, updateOrg } = useContext(OrgContext);
  const { selectedColumn, setSelectedColumn, setSearchText, setDisableSearch } =
    useContext(SearchContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  useAppTitle("MCPTT Groups- IWF Config Admin Tool (ICAT) by AT&T");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [groupsData, setGroupsData] = useState<any[]>([]);

  // State to check if Bulk Upload is Clicked
  const [isBulkUploadEnabled, setIsBulkUploadEnabled] = useState(false);

  const {
    showSidePanel,
    showAdvancedSearch,
    handleToggleAdvanceSearchPanel,
    handleToggleSidePanel,
  } = useHideSidePanel();
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    string | null
  >(null);

  const [searchData, setSearchData] = useState<ISearchData>(INITIAL_STATE);
  const [numberOfRows, setNumberOfRows] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);

  const [searchQuery, setSearhQuery] = useState<{
    data: ISearchData;
    pageNum: number;
    pageSize: number;
    sortByField?: string;
    sortDirection?: "desc" | "asc";
  }>({
    pageSize: 20,
    pageNum: 1,
    data: {},
    sortByField: undefined,
    sortDirection: undefined,
  });

  const fetchMCPTTGroups = async () => {
    setLoading(true);
    try {
      if (selectedOrg) {
        const { data, pageNum, pageSize, sortByField, sortDirection } =
          searchQuery;
        const queryParams = new URLSearchParams({
          page: `${pageNum}`,
          pageSize: `${pageSize}`,
          ...(sortByField && { sortByField }),
          ...(sortDirection && { sortDirection }),
          ...(data &&
            data["MCPTT-GID"] && {
              "MCPTT-GID": data["MCPTT-GID"],
            }),
          ...(data &&
            data["MAPPED-P25-TG-ID"] && {
              "MAPPED-P25-TG-ID": data["MAPPED-P25-TG-ID"],
            }),
          ...(data &&
            data["MAPPED-DMR-TG-ID"] && {
              "MAPPED-DMR-TG-ID": data["MAPPED-DMR-TG-ID"],
            }),
          ...(data &&
            data["MAPPED-ROIP-TG-ID"] && {
              "MAPPED-ROIP-TG-ID": data["MAPPED-ROIP-TG-ID"],
            }),
          ...(data &&
            data["MAPPED-DFSI-TG-ID"] && {
              "MAPPED-DFSI-TG-ID": data["MAPPED-DFSI-TG-ID"],
            }),
        });
        const response = await makeApiRequest<{
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Rows: Array<any>;
          NbrOfRows: number;
          NbrOfPages: number;
        }>({
          path: `/api/v1/mc-groups?${queryParams}`,
          token,
          org: selectedOrg.id,
        });
        setNumberOfRows(response.NbrOfRows);
        setNumberOfPages(response.NbrOfPages);
        setGroupsData(response.Rows);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedColumn(null);
    setSearchText("");
    setDisableSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchData(INITIAL_STATE);
    setSearhQuery({
      pageSize: 20,
      pageNum: 1,
      data: {},
      sortByField: undefined,
      sortDirection: undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg]);

  useEffect(() => {
    fetchMCPTTGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, searchQuery]);

  useEffect(() => {
    if (!showAdvancedSearch && Object.keys(searchQuery.data).length === 0) {
      setSearchData(INITIAL_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdvancedSearch]);

  const sortTable = (col: string, direction: "asc" | "desc") => () => {
    setSearhQuery({
      ...searchQuery,
      sortByField: col,
      sortDirection: direction,
    });
  };

  const handleMCPTTGroupDelete = async (ref: string) => {
    if (selectedOrg && token) {
      try {
        await deleteMCPTTGroup({
          ref,
          org: selectedOrg.id,
          token,
        });
        successAlert(DELETE_MCPTT_GROUP_TRANSLATIONS.successAlertMessage);
        handleToggleSidePanel();
        setSelectedConnectionId(null);
        fetchMCPTTGroups();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        errorAlert(error);
      }
    }
  };

  return (
    <>
      <SectionHeader
        pageTitle={READ_MCPTT_GROUP_TRANSLATIONS.header}
        pageDescription={READ_MCPTT_GROUP_TRANSLATIONS.headerSubtext}
        sideComponent={
          <div className="dropdown-details">
            <ESOganisationDropdown
              org={selectedOrg || undefined}
              orgsList={orgList}
              setOrg={updateOrg}
            />
          </div>
        }
      />

      {selectedOrg ? (
        <>
          {showAdvancedSearch ? (
            <AdvancedSearch
              setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
              advancedSearchState={searchData}
              advancedSearchConfig={searchConfigNew}
              setAdvancedSearchState={setSearchData}
              onSubmit={(data) =>
                setSearhQuery({
                  ...searchQuery,
                  pageNum: 1,
                  pageSize: 20,
                  data,
                })
              }
              onCancel={() => {
                setSearchData(INITIAL_STATE);
                handleToggleAdvanceSearchPanel();
              }}
            />
          ) : null}

          {showSidePanel && isBulkUploadEnabled ? (
            <BulkUpload
              moduleName="MCPTT Group"
              pathName="/api/v1/mc-groups"
              csvTemplateFile={[
                "MCPTT-GID,MAPPED-P25-TG-ID,MAPPED-DFSI-TG-ID,MAPPED-ROIP-TG-ID,MAPPED-DMR-TG-ID",
              ]}
              closeBulkUploadSidePanel={() => {
                handleToggleSidePanel();
                setIsBulkUploadEnabled(false);
                fetchMCPTTGroups();
              }}
            />
          ) : null}

          {showSidePanel &&
          selectedOrg &&
          selectedConnectionId &&
          !isBulkUploadEnabled ? (
            <EditGroup
              panelName={READ_MCPTT_GROUP_TRANSLATIONS.editRow.panelName}
              initialGroupState={INITIAL_MCPTT_GROUPS_EDIT_STATE}
              initialGroupProfileState={INITIAL_MCPTT_GROUP_PROFILE_EDIT_STATE}
              org={selectedOrg.id}
              esRef={selectedConnectionId}
              setShowEditPanel={handleToggleSidePanel}
              onDelete={() => handleMCPTTGroupDelete(selectedConnectionId)}
              groupFormFields={EDIT_MCPTT_GROUPS_FORM_FIELDS}
              groupProfileFormFields={EDIT_MCPTT_GROUP_PROFILE_FORM_FIELDS}
              transformGroupProfileUpdateBody={
                transformMCPTTGroupProfileUpdateBody
              }
              transformGroupsUpdateBody={transformMCPTTGroupsUpdateBody}
              header={READ_MCPTT_GROUP_TRANSLATIONS.editRow.header}
              IcCheckAPI={MCGroupsEdit}
              onGroupUpdate={updateMCPTTGroupByRef}
              onGroupProfileUpdate={updateMCPTTGroupProfileByRef}
              loadDataByRef={getMCPTTGroupDetailsByRef}
              fetchGroupData={getMCPTTGroupByRef}
              fetchGroupProfileData={getMCPTTGroupProfileByRef}
              hasDeletePermission={mcpttGroups && mcpttGroups.includes("d")}
              hasGroupEditPermission={mcpttGroups && mcpttGroups.includes("u")}
              hasGroupProfileEditPermission={
                mcpttGroupProfile && mcpttGroupProfile.includes("u")
              }
              hasGroupProfileViewPermission={
                mcpttGroupProfile && mcpttGroupProfile.includes("r")
              }
              refreshTable={fetchMCPTTGroups}
              deleteModalItemName="MCPTT-GID"
            />
          ) : null}

          <DataTableWithControls
            loading={loading}
            filterColumns={MCPTT_GROUPS_FILTER_COLUMNS}
            selectedColumn={selectedColumn}
            setSelectedColumn={setSelectedColumn}
            onSearch={(term) =>
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: { [`${selectedColumn?.id}`]: term },
              })
            }
            setShowAdvancedSearch={handleToggleAdvanceSearchPanel}
            showCreateButton={mcpttGroups && mcpttGroups.includes("c")}
            onCreateButtonClick={() => {
              navigate(`/configurationManagement/mcpttGroups/new`);
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rows={transformMCPTTGroupsRows(groupsData).map((row: any) => ({
              ...row,
              action: (
                <TableActionButton
                  onActionButtonClick={() => {
                    setSelectedConnectionId(row.id);
                    handleToggleSidePanel();
                  }}
                />
              ),
            }))}
            numberOfAdvancedSearchFilters={searchConfigNew.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc, el: any) => {
                const { name } = el;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const obj: any = { ...searchData };
                return acc + (obj[name] && obj[name].length > 0 ? 1 : 0);
              },
              0
            )}
            clearAdvancedSearchFilters={() => {
              setSearchData(INITIAL_STATE);
              setSearhQuery({
                ...searchQuery,
                pageNum: 1,
                pageSize: 20,
                data: {},
              });
            }}
            headers={MCPTT_GROUPS_TABLE_HEADERS}
            pageNum={searchQuery.pageNum}
            setPageNum={(pageNum) => setSearhQuery({ ...searchQuery, pageNum })}
            totalItems={numberOfRows}
            totalPages={numberOfPages}
            pageSize={searchQuery.pageSize}
            setPageSize={(pageSize) =>
              setSearhQuery({ ...searchQuery, pageSize })
            }
            sortByField={searchQuery.sortByField}
            sortDirection={searchQuery.sortDirection}
            handleSort={sortTable}
            showBulkUploadButton={mcpttGroups && mcpttGroups.includes("c")}
            onBulkUploadButtonClick={() => {
              handleToggleSidePanel();
              setIsBulkUploadEnabled(true);
            }}
          />
        </>
      ) : (
        <InfoAlert
          heading={READ_MCPTT_GROUP_TRANSLATIONS.infoAlert.heading}
          subText={READ_MCPTT_GROUP_TRANSLATIONS.infoAlert.subText}
        />
      )}
    </>
  );
};

export default MCPTTGroups;
